// /app/frontend/src/components/QuoteOrderProgress.jsx
// Unified Order Progress component for both user dashboard and admin panel
// Shows the complete workflow: Created → Approved → Sales Order → Invoiced → Paid → Production → Fulfillment → Delivered

import { useState } from 'react';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';

const QuoteOrderProgress = ({ 
  quote,
  compact = false,
  expandable = true,
  showLabels = true
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine step statuses based on quote data
  const getStepStatus = () => {
    const status = quote?.status || 'pending';
    
    // Step 1: Created - always complete if quote exists
    const isCreated = true;
    const createdByAdmin = quote?.created_by_role === 'admin' || quote?.created_by_name === 'Sales Team';
    const creatorName = createdByAdmin ? 'Sales Team' : (quote?.created_by_name || '');
    
    // Step 2: Quote Approved
    const quoteApproved = ['client_approved', 'signed', 'converted_to_so', 'converted', 'invoiced', 'approved', 'production', 'scheduled', 'completed'].includes(status);
    const quotePending = status === 'awaiting_approval';
    const approvedByAdmin = quote?.approved_by_role === 'admin' || quote?.approved_by === 'Sales Team';
    const approverName = quoteApproved 
      ? (approvedByAdmin ? 'Sales Team' : (quote?.approved_by || quote?.customer_name || 'Customer'))
      : '';
    
    // Step 3: Sales Order Created
    const hasSalesOrder = status === 'converted_to_so' || quote?.sales_order_id || ['converted', 'invoiced', 'production', 'scheduled', 'completed'].includes(status);
    const soCreatedByAdmin = quote?.so_created_by_role === 'admin';
    const soCreatorName = hasSalesOrder 
      ? (soCreatedByAdmin ? 'Sales Team' : (quote?.so_created_by || ''))
      : '';
    
    // Step 4: Sales Order Approved  
    const soApproved = quote?.so_approved || quote?.so_approved_at || ['converted', 'invoiced', 'production', 'scheduled', 'completed'].includes(status);
    const soApprovedByAdmin = quote?.so_approved_by_role === 'admin' || quote?.so_approved_by === 'Sales Team';
    const soApproverName = soApproved 
      ? (soApprovedByAdmin ? 'Sales Team' : (quote?.so_approved_by || quote?.customer_name || 'Customer'))
      : '';
    
    // Step 5: Invoiced
    const hasInvoice = status === 'invoiced' || status === 'converted' || quote?.invoice_id || ['production', 'scheduled', 'completed'].includes(status);
    
    // Step 6: Paid
    const isPaid = quote?.payment_status === 'paid' || quote?.invoice_paid;
    const isPartialPaid = quote?.payment_status === 'partial';
    const paidDate = isPaid && quote?.paid_at ? new Date(quote.paid_at).toLocaleDateString() : '';
    
    // Step 7: Production
    const productionComplete = quote?.production_status === 'completed' || status === 'completed';
    const productionInProgress = quote?.production_status === 'in_production' || status === 'production';
    
    // Step 8: Fulfillment/Delivery
    const isDelivered = ['delivered', 'picked_up'].includes(quote?.fulfillment_status) || status === 'completed';
    const isShipped = ['shipped', 'in_transit', 'ready_for_pickup'].includes(quote?.fulfillment_status);
    const fulfillmentType = quote?.fulfillment_type || quote?.fulfillment_method || 'shipping';
    const fulfillmentLabel = fulfillmentType === 'pickup' ? 'Pick-up' :
                             fulfillmentType === 'delivery' ? 'Local Delivery' : 'Shipped';

    return {
      created: { done: isCreated, by: creatorName, isAdmin: createdByAdmin },
      quoteApproved: { done: quoteApproved, pending: quotePending, by: approverName, isAdmin: approvedByAdmin },
      salesOrder: { done: hasSalesOrder, by: soCreatorName, isAdmin: soCreatedByAdmin },
      soApproved: { done: soApproved, pending: hasSalesOrder && !soApproved, by: soApproverName, isAdmin: soApprovedByAdmin },
      invoiced: { done: hasInvoice },
      paid: { done: isPaid, partial: isPartialPaid, date: paidDate },
      production: { done: productionComplete, inProgress: productionInProgress },
      delivered: { done: isDelivered, shipped: isShipped, type: fulfillmentLabel }
    };
  };

  const steps = getStepStatus();

  // Calculate mini dots for compact view
  const getMiniDots = () => [
    steps.created.done,
    steps.quoteApproved.done,
    steps.salesOrder.done,
    steps.production.done || steps.production.inProgress,
    steps.delivered.done
  ];

  const miniDots = getMiniDots();

  // Compact view - just shows mini dots with expand toggle
  if (compact && !isExpanded) {
    return (
      <div className="mt-3">
        <button
          onClick={() => expandable && setIsExpanded(true)}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-neutral-100 hover:bg-neutral-200 rounded-lg cursor-pointer transition-colors"
          data-testid="order-progress-compact"
        >
          <div className="flex items-center gap-1">
            {miniDots.map((done, idx) => (
              <div 
                key={idx}
                className={`w-2 h-2 rounded-full ${
                  done ? 'bg-green-500' : 
                  (idx === 3 && steps.production.inProgress) ? 'bg-amber-500' : 
                  'bg-neutral-300'
                }`}
              />
            ))}
          </div>
          {showLabels && <span className="text-xs font-medium text-neutral-600">Order Progress</span>}
          {expandable && <ChevronDown className="w-3 h-3 text-neutral-400" />}
        </button>
      </div>
    );
  }

  // Expanded timeline view
  const renderExpandedTimeline = () => (
    <div className="relative bg-white border border-neutral-200 rounded-lg shadow-lg p-4 w-full" data-testid="order-progress-expanded">
      {expandable && compact && (
        <button 
          onClick={() => setIsExpanded(false)}
          className="absolute top-2 right-2 text-neutral-400 hover:text-neutral-600"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
      )}
      
      <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-4">Order Progress</p>
      
      {/* Timeline Container */}
      <div className="relative overflow-x-auto">
        <div className="flex items-start min-w-[700px]">
          
          {/* Step 1: Created */}
          <div className="flex flex-col items-center min-w-[70px]">
            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
              <Check className="w-4 h-4 text-white" />
            </div>
            <span className="text-xs font-medium text-green-700 mt-1 text-center">Created</span>
            {steps.created.by && (
              <span className="text-[10px] text-neutral-500 text-center">by {steps.created.by}</span>
            )}
          </div>
          
          {/* Connector: Quote Approval */}
          <div className="flex-1 flex flex-col items-center mx-1 min-w-[60px]">
            <span className={`text-[10px] font-medium mb-1 ${
              steps.quoteApproved.done ? 'text-green-600' : 
              steps.quoteApproved.pending ? 'text-amber-600' : 'text-neutral-400'
            }`}>
              {steps.quoteApproved.done ? '✓ Approved' : steps.quoteApproved.pending ? '⏳ Pending' : 'Approved'}
            </span>
            <div className={`w-full h-1 rounded ${
              steps.quoteApproved.done ? 'bg-green-500' : 
              steps.quoteApproved.pending ? 'bg-amber-400' : 'bg-neutral-300'
            }`} />
            {steps.quoteApproved.done && steps.quoteApproved.by && (
              <span className="text-[10px] text-green-600 mt-1">by {steps.quoteApproved.by}</span>
            )}
          </div>
          
          {/* Step 2: Sales Order */}
          <div className="flex flex-col items-center min-w-[70px]">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              steps.salesOrder.done ? 'bg-green-500' : 'bg-neutral-300'
            }`}>
              {steps.salesOrder.done ? (
                <Check className="w-4 h-4 text-white" />
              ) : (
                <span className="text-white text-xs font-bold">2</span>
              )}
            </div>
            <span className={`text-xs font-medium mt-1 text-center ${
              steps.salesOrder.done ? 'text-green-700' : 'text-neutral-400'
            }`}>Sales Order</span>
            {steps.salesOrder.done && steps.salesOrder.by && (
              <span className="text-[10px] text-neutral-500 text-center">by {steps.salesOrder.by}</span>
            )}
          </div>
          
          {/* Connector: SO Approval */}
          <div className="flex-1 flex flex-col items-center mx-1 min-w-[60px]">
            <span className={`text-[10px] font-medium mb-1 ${
              steps.soApproved.done ? 'text-green-600' : 
              steps.soApproved.pending ? 'text-amber-600' : 'text-neutral-400'
            }`}>
              {steps.soApproved.done ? '✓ Approved' : steps.soApproved.pending ? '⏳ Pending' : 'Approved'}
            </span>
            <div className={`w-full h-1 rounded ${
              steps.soApproved.done ? 'bg-green-500' : 
              steps.soApproved.pending ? 'bg-amber-400' : 'bg-neutral-300'
            }`} />
            {steps.soApproved.done && steps.soApproved.by && (
              <span className="text-[10px] text-green-600 mt-1">by {steps.soApproved.by}</span>
            )}
          </div>
          
          {/* Step 3: Invoiced */}
          <div className="flex flex-col items-center min-w-[70px]">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              steps.invoiced.done ? 'bg-green-500' : 'bg-neutral-300'
            }`}>
              {steps.invoiced.done ? (
                <Check className="w-4 h-4 text-white" />
              ) : (
                <span className="text-white text-xs font-bold">3</span>
              )}
            </div>
            <span className={`text-xs font-medium mt-1 text-center ${
              steps.invoiced.done ? 'text-green-700' : 'text-neutral-400'
            }`}>Invoiced</span>
          </div>
          
          {/* Connector: Paid */}
          <div className="flex-1 flex flex-col items-center mx-1 min-w-[60px]">
            <span className={`text-[10px] font-medium mb-1 ${
              steps.paid.done ? 'text-green-600' : 
              steps.paid.partial ? 'text-amber-600' : 'text-neutral-400'
            }`}>
              {steps.paid.done ? '✓ Paid' : steps.paid.partial ? '⏳ Partial' : 'Paid'}
            </span>
            <div className={`w-full h-1 rounded ${
              steps.paid.done ? 'bg-green-500' : 
              steps.paid.partial ? 'bg-amber-400' : 'bg-neutral-300'
            }`} />
            {steps.paid.done && steps.paid.date && (
              <span className="text-[10px] text-neutral-500 mt-1">{steps.paid.date}</span>
            )}
          </div>
          
          {/* Step 4: Production */}
          <div className="flex flex-col items-center min-w-[70px]">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              steps.production.done ? 'bg-green-500' : 
              steps.production.inProgress ? 'bg-amber-500' : 'bg-neutral-300'
            }`}>
              {steps.production.done ? (
                <Check className="w-4 h-4 text-white" />
              ) : (
                <span className="text-white text-xs font-bold">4</span>
              )}
            </div>
            <span className={`text-xs font-medium mt-1 text-center ${
              steps.production.done ? 'text-green-700' : 
              steps.production.inProgress ? 'text-amber-700' : 'text-neutral-400'
            }`}>Production</span>
          </div>
          
          {/* Connector: Fulfillment */}
          <div className="flex-1 flex flex-col items-center mx-1 min-w-[60px]">
            <span className={`text-[10px] font-medium mb-1 ${
              steps.delivered.done ? 'text-green-600' : 
              steps.delivered.shipped ? 'text-blue-600' : 'text-neutral-400'
            }`}>
              {steps.delivered.done ? '✓ Fulfillment' : steps.delivered.shipped ? '🚚 Fulfillment' : 'Fulfillment'}
            </span>
            <div className={`w-full h-1 rounded ${
              steps.delivered.done ? 'bg-green-500' : 
              steps.delivered.shipped ? 'bg-blue-500' : 'bg-neutral-300'
            }`} />
            {(steps.delivered.shipped || steps.delivered.done) && (
              <span className="text-[10px] text-neutral-500 mt-1">{steps.delivered.type}</span>
            )}
          </div>
          
          {/* Step 5: Delivered */}
          <div className="flex flex-col items-center min-w-[70px]">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              steps.delivered.done ? 'bg-green-500' : 
              steps.delivered.shipped ? 'bg-blue-500' : 'bg-neutral-300'
            }`}>
              {steps.delivered.done ? (
                <Check className="w-4 h-4 text-white" />
              ) : (
                <span className="text-white text-xs font-bold">5</span>
              )}
            </div>
            <span className={`text-xs font-medium mt-1 text-center ${
              steps.delivered.done ? 'text-green-700' : 'text-neutral-400'
            }`}>
              {quote?.fulfillment_status === 'picked_up' ? 'Picked Up' : 'Delivered'}
            </span>
          </div>
          
        </div>
      </div>
    </div>
  );

  // Non-compact view or expanded compact
  if (!compact || isExpanded) {
    if (compact && expandable) {
      return (
        <div className="mt-3 relative">
          <button
            onClick={() => setIsExpanded(false)}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100 hover:bg-amber-200 rounded-lg cursor-pointer transition-colors mb-2"
          >
            <div className="flex items-center gap-1">
              {miniDots.map((done, idx) => (
                <div 
                  key={idx}
                  className={`w-2 h-2 rounded-full ${
                    done ? 'bg-green-500' : 
                    (idx === 3 && steps.production.inProgress) ? 'bg-amber-500' : 
                    'bg-neutral-300'
                  }`}
                />
              ))}
            </div>
            {showLabels && <span className="text-xs font-medium text-neutral-600">Order Progress</span>}
            <ChevronUp className="w-3 h-3 text-neutral-400" />
          </button>
          {renderExpandedTimeline()}
        </div>
      );
    }
    return renderExpandedTimeline();
  }

  return null;
};

export default QuoteOrderProgress;
